import React, { useState, useEffect, useCallback } from 'react';
import { ENV_CONFIG } from '../utils/env';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

interface User {
  id: number;
  email: string;
  hasSpun: boolean;
  isAllowedToSpin: boolean;
  emailSent: boolean;
  quillformClicked: boolean;
  prize?: Prize;
  createdAt: Date;
  updatedAt: Date;
}

interface Prize {
  id: number;
  name: string;
  description: string;
  value: number;
  createdAt: Date;
}

const Statistics: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get API URL from environment - NO HARDCODING!
  const API_BASE_URL = ENV_CONFIG.BACKOFFICE_URL;

  const fetchData = useCallback(async () => {
    try {
      const [usersResponse, prizesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users`),
        fetch(`${API_BASE_URL}/api/prizes`)
      ]);

      if (usersResponse.ok && prizesResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.data || []);
        setError(null); // Clear any previous errors
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      setError('Erreur lors de la récupération des données. Vérifiez que le backend est en cours d\'exécution.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Early return if no API URL configured - AFTER all hooks
  if (!API_BASE_URL) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">Configuration Error</div>
          <div className="text-gray-600">REACT_APP_BACKOFFICE_URL environment variable is not set</div>
          <div className="text-sm text-gray-500 mt-2">Check your environment configuration</div>
        </div>
      </div>
    );
  }

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getRecentUsers = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return usersArray.filter(user => new Date(user.createdAt) > thirtyDaysAgo);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Safety checks to ensure users are an array
  const usersArray = Array.isArray(users) ? users : [];
  
  // Key Statistics
  const totalUsers = usersArray.length;
  const spunUsers = usersArray.filter(user => user.hasSpun).length;
  const usersWhoWon = usersArray.filter(user => user.hasSpun && user.prize && user.prize.name !== 'Rien').length;
  const usersWhoDidntWin = usersArray.filter(user => user.hasSpun && (!user.prize || user.prize.name === 'Rien')).length;
  const usersWhoReceivedEmails = usersArray.filter(user => user.emailSent).length;
  const usersWhoClickedApply = usersArray.filter(user => user.quillformClicked).length;
  
  // Additional Statistics
  const recentUsers = getRecentUsers();

  // Chart Data
  const engagementData = [
    { name: 'Ont tourné', value: spunUsers, color: '#3B82F6' },
    { name: 'Ont gagné', value: usersWhoWon, color: '#10B981' },
    { name: "N'ont pas gagné", value: usersWhoDidntWin, color: '#F59E0B' },
    { name: 'Ont reçu email', value: usersWhoReceivedEmails, color: '#8B5CF6' }
  ];

  const dailyActivityData = getDailyActivityData(usersArray);
  const prizeDistributionData = getPrizeDistributionData(usersArray);

  // Helper functions for chart data
  function getDailyActivityData(users: User[]) {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.setHours(0, 0, 0, 0));
      const dayEnd = new Date(date.setHours(23, 59, 59, 999));
      
      // Count users who spun the wheel on this day
      const daySpins = users.filter(user => {
        // We need to track when users spun, but since we don't have a spin date field,
        // we'll use the updatedAt field for users who have spun
        if (!user.hasSpun) return false;
        const userDate = new Date(user.updatedAt || user.createdAt);
        return userDate >= dayStart && userDate <= dayEnd;
      });
      
      // Count users who clicked Quillform on this day
      const dayApplications = users.filter(user => {
        if (!user.quillformClicked) return false;
        const userDate = new Date(user.updatedAt || user.createdAt);
        return userDate >= dayStart && userDate <= dayEnd;
      });
      
      last7Days.push({
        date: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
        tours: daySpins.length,
        applications: dayApplications.length
      });
    }
    return last7Days;
  }

  function getPrizeDistributionData(users: User[]) {
    const prizeCounts: { [key: string]: number } = {};
    users.forEach(user => {
      if (user.hasSpun && user.prize) {
        const prizeName = user.prize.name;
        prizeCounts[prizeName] = (prizeCounts[prizeName] || 0) + 1;
      }
    });
    
    return Object.entries(prizeCounts).map(([name, value]) => ({
      name,
      value,
      color: getRandomColor()
    }));
  }

  function getRandomColor() {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Statistiques</h1>
        <p className="text-sm text-gray-600 mt-1">Vue d'ensemble de l'activité de l'application</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Users Who Spun */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ont tourné la roue</p>
                <p className="text-2xl font-semibold text-gray-900">{spunUsers}</p>
                <p className="text-xs text-gray-500">{totalUsers > 0 ? Math.round((spunUsers / totalUsers) * 100) : 0}% des utilisateurs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Who Won */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ont gagné un prix</p>
                <p className="text-2xl font-semibold text-gray-900">{usersWhoWon}</p>
                <p className="text-xs text-gray-500">{spunUsers > 0 ? Math.round((usersWhoWon / spunUsers) * 100) : 0}% des tours</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Who Didn't Win */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">N'ont pas gagné</p>
                <p className="text-2xl font-semibold text-gray-900">{usersWhoDidntWin}</p>
                <p className="text-xs text-gray-500">{spunUsers > 0 ? Math.round((usersWhoDidntWin / spunUsers) * 100) : 0}% des tours</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Who Clicked Apply */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ont cliqué sur Postuler</p>
                <p className="text-2xl font-semibold text-gray-900">{usersWhoClickedApply}</p>
                <p className="text-xs text-gray-500">Utilisateurs engagés</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Who Received Emails */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ont reçu leur email</p>
                <p className="text-2xl font-semibold text-gray-900">{usersWhoReceivedEmails}</p>
                <p className="text-xs text-gray-500">{usersWhoWon > 0 ? Math.round((usersWhoReceivedEmails / usersWhoWon) * 100) : 0}% des gagnants</p>
              </div>
            </div>
          </div>
        </div>

        {/* Total Users */}
        <div className="bg-white overflow-hidden shadow-sm border border-gray-200 rounded-lg">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-500 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Utilisateurs</p>
                <p className="text-2xl font-semibold text-gray-900">{totalUsers}</p>
                <p className="text-xs text-gray-500">Utilisateurs inscrits</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Overview - Bar Chart */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Vue d'ensemble de l'engagement</h3>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Prize Distribution - Pie Chart */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Distribution des prix gagnés</h3>
          </div>
          <div className="p-6">
            {prizeDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={prizeDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prizeDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                Aucun prix gagné pour le moment
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Activity Chart */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Activité des 7 derniers jours</h3>
        </div>
        <div className="p-6">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyActivityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="tours" stroke="#3B82F6" strokeWidth={2} name="Tours de roue" />
              <Line type="monotone" dataKey="applications" stroke="#10B981" strokeWidth={2} name="Candidatures" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Activité Récente</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {recentUsers.slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    <p className="text-xs text-gray-500">Inscrit le {formatDate(user.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {user.hasSpun ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      A tourné
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      N'a pas tourné
                    </span>
                  )}
                </div>
              </div>
            ))}
            {recentUsers.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">Aucune activité récente</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;

